// Local, per-teacher lesson progress. This is what the teacher sees: every
// action is written here first and the screen updates from here, whether or
// not the network is available.
//
// A record gains a stable `syncKey` the first time its lesson is started. That
// key — never a title, timestamp or slug — is the identity of the matching
// delivery on the server, so a retry can never produce a second one.
//
// Each lesson has one current record: the delivery being taught now, or the
// last one finished. Teaching a finished lesson again moves that finished
// delivery into `past`, still under its own syncKey, so it stays completed and
// keeps syncing while the new delivery gets an identity of its own.

import { readJson, remove, writeJson } from "./storage";

// Every function below takes an `owner`: one teacher teaching one class, from
// progressOwner(). The same lesson taught to two classes is two deliveries.
const key = (owner) => `progress:${owner}`;
const pastKey = (owner) => `progress-past:${owner}`;

export const progressOwner = (userId, classroomId) => `${userId}@${classroomId}`;

// Before classes came from data, progress was kept per teacher only and every
// delivery went to the single pilot class. Moved once into `owner`; anything
// already there wins. Only called when the teacher has exactly one class.
export const adoptLegacyProgress = (userId, owner) => {
  for (const k of [key, pastKey]) {
    const legacy = readJson(k(userId), null);
    if (!legacy) continue;
    writeJson(k(owner), { ...legacy, ...readJson(k(owner), {}) });
    remove(k(userId));
  }
};

export const IN_PROGRESS = "in_progress";
export const COMPLETED = "completed";

const uuidFromRandomBytes = () => {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
};

// crypto.randomUUID is missing on older Android WebViews and on insecure origins.
export const newSyncKey = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : uuidFromRandomBytes();

export const getAllProgress = (userId) => readJson(key(userId), {});

export const getProgress = (userId, lessonId) =>
  getAllProgress(userId)[lessonId] || null;

const write = (userId, lessonId, next) => {
  const all = getAllProgress(userId);
  writeJson(key(userId), { ...all, [lessonId]: next });
  return next;
};

// A teacher action. `rev` counts them: two actions in the same millisecond
// share a timestamp, so the clock cannot tell the server which change it
// actually accepted.
const update = (userId, lessonId, changes) => {
  const existing = getProgress(userId, lessonId);
  return write(userId, lessonId, {
    ...existing,
    ...changes,
    rev: (existing?.rev || 0) + 1,
    updatedAt: new Date().toISOString(),
  });
};

export const startLesson = (userId, lessonId, blockSort = null) => {
  const existing = getProgress(userId, lessonId);
  if (existing) {
    // Resuming keeps the teacher's place and the delivery identity already in
    // use; a record saved before deliveries existed just gains one.
    return existing.syncKey
      ? existing
      : update(userId, lessonId, { syncKey: newSyncKey() });
  }
  return update(userId, lessonId, {
    syncKey: newSyncKey(),
    status: IN_PROGRESS,
    step: 0,
    blockSort,
    startedAt: new Date().toISOString(),
    completedAt: null,
  });
};

// Starts a new delivery of a lesson the teacher has already finished. The
// finished one is kept, unchanged, under its own syncKey.
export const teachAgain = (userId, lessonId, blockSort = null) => {
  const existing = getProgress(userId, lessonId);
  if (existing?.status !== COMPLETED) {
    return startLesson(userId, lessonId, blockSort);
  }
  if (existing.syncKey) {
    const past = getPast(userId);
    writeJson(pastKey(userId), {
      ...past,
      [existing.syncKey]: { ...existing, lessonId },
    });
  }
  const all = getAllProgress(userId);
  delete all[lessonId];
  writeJson(key(userId), all);
  return startLesson(userId, lessonId, blockSort);
};

export const setStep = (userId, lessonId, step, blockSort = null) => {
  const existing = getProgress(userId, lessonId);
  // A finished lesson is never walked back into "in progress".
  if (existing?.status === COMPLETED) return existing;
  return update(userId, lessonId, { status: IN_PROGRESS, step, blockSort });
};

export const completeLesson = (userId, lessonId, blockSort = null) => {
  const existing = getProgress(userId, lessonId);
  // Confirming twice keeps the first completion time.
  if (existing?.status === COMPLETED) return existing;
  return update(userId, lessonId, {
    status: COMPLETED,
    completedAt: new Date().toISOString(),
    ...(blockSort === null ? {} : { blockSort }),
  });
};

// What the teacher is told. The school's records hold this lesson only once
// the server has acknowledged the change last written here. Progress saved
// before deliveries existed has no identity on the server, so it never counts
// as synced until the teacher opens the lesson again.
export const isSynced = (record) =>
  Boolean(record?.syncKey) && (record.syncedRev || 0) >= (record.rev || 0);

// What the sync loop works through: only records that have an identity to
// synchronise under.
export const needsSync = (record) =>
  Boolean(record?.syncKey) && (record.syncedRev || 0) < (record.rev || 0);

// Finished deliveries that a later delivery of the same lesson replaced.
const getPast = (userId) => readJson(pastKey(userId), {});

// Current and past deliveries that still have a change for the server.
export const pendingEntries = (userId) => [
  ...Object.entries(getAllProgress(userId)).filter(([, record]) =>
    needsSync(record)
  ),
  ...Object.values(getPast(userId))
    .filter(needsSync)
    .map(({ lessonId, ...record }) => [lessonId, record]),
];

// Records which local change the server accepted, on the delivery with that
// syncKey — which may have become a past delivery while the request was in
// flight. Deliberately does not touch `rev`, so a change made meanwhile stays
// pending instead of being silently marked as saved.
export const markSynced = (userId, lessonId, rev, syncKey) => {
  const bump = (record) => ({
    ...record,
    syncedRev: Math.max(record.syncedRev || 0, rev),
  });
  const existing = getProgress(userId, lessonId);
  if (existing && (!syncKey || existing.syncKey === syncKey)) {
    return write(userId, lessonId, bump(existing));
  }
  const past = getPast(userId);
  if (!syncKey || !past[syncKey]) return null;
  const next = bump(past[syncKey]);
  writeJson(pastKey(userId), { ...past, [syncKey]: next });
  return next;
};
